<template>
  <Teleport to="body">
    <div
      :class="$style.overlay"
      @click.self="handleClose"
    >
      <div :class="$style.modalContainer">
        <div :class="$style.modal">
          <div :class="$style.header">
            <h2 :class="$style.headerTitle">
              {{
                isEditing
                  ? `Stage ${stageNumber}: ${formData.name || 'Edit Stage'}`
                  : 'Add New Stage'
              }}
            </h2>
            <button
              :class="$style.closeButton"
              @click="handleClose"
            >
              <CloseIcon :class="$style.closeIcon" />
            </button>
          </div>
          <ImpactModelSummary
            v-if="previewImpact"
            :result="previewImpact"
            compact
            eyebrow="Live preview · unsaved changes"
            title="Estimated impact of the whole plan"
          />
          <p
            v-else
            :class="$style.previewHint"
          >
            Enter the usual-process estimates to see live impact while editing.
          </p>
          <div :class="$style.container">
            <div>
              <form
                :class="$style.form"
                @submit.prevent="handleSave"
              >
                <div :class="$style.fieldGroup">
                  <label :class="$style.label">
                    <span :class="$style.questionNumber">1.</span>
                    Interview stage Name/Title
                    <span :class="$style.required">*</span>
                  </label>
                  <input
                    v-model="formData.name"
                    type="text"
                    :class="$style.input"
                    placeholder="Technical Interview"
                  />
                </div>

                <div :class="$style.fieldGroup">
                  <label :class="$style.label">
                    <span :class="$style.questionNumber">2.</span>
                    What is the stage's primary focus area/s?
                    <span :class="$style.required">*</span>
                    <InfoIcon :class="$style.infoIcon" />
                  </label>

                  <!-- Custom dropdown for checkboxes -->
                  <div
                    :class="$style.customDropdown"
                    data-custom-dropdown
                  >
                    <div
                      :class="[
                        $style.dropdownTrigger,
                        isFocusAreaOpen && $style.dropdownOpen,
                      ]"
                      @click="toggleFocusAreaDropdown"
                    >
                      <span :class="$style.dropdownPlaceholder">
                        {{ selectedFocusAreasText || 'Select focus areas' }}
                      </span>
                      <svg
                        :class="[
                          $style.dropdownArrow,
                          isFocusAreaOpen && $style.arrowRotated,
                        ]"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M6 9L12 15L18 9"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>

                    <div
                      v-if="isFocusAreaOpen"
                      :class="$style.dropdownContent"
                    >
                      <div :class="$style.dropdownHeader">
                        <span :class="$style.dropdownTitle"
                          >Select focus areas</span
                        >
                        <button
                          type="button"
                          :class="$style.clearAllButton"
                          @click.stop="clearAllFocusAreas"
                        >
                          Clear all
                        </button>
                      </div>

                      <div :class="$style.checkboxOptionsContainer">
                        <label :class="$style.checkboxOption">
                          <input
                            v-model="formData.stageFocusArea"
                            type="checkbox"
                            value="technical-interview"
                            :class="$style.checkbox"
                          />
                          <span :class="$style.checkboxLabel"
                            >Technical Skills</span
                          >
                        </label>

                        <label :class="$style.checkboxOption">
                          <input
                            v-model="formData.stageFocusArea"
                            type="checkbox"
                            value="cultural-fit"
                            :class="$style.checkbox"
                          />
                          <span :class="$style.checkboxLabel"
                            >Cultural Fit</span
                          >
                        </label>

                        <label :class="$style.checkboxOption">
                          <input
                            v-model="formData.stageFocusArea"
                            type="checkbox"
                            value="behavioral"
                            :class="$style.checkbox"
                          />
                          <span :class="$style.checkboxLabel"
                            >Behavioral Skills</span
                          >
                        </label>

                        <label :class="$style.checkboxOption">
                          <input
                            v-model="formData.stageFocusArea"
                            type="checkbox"
                            value="leadership"
                            :class="$style.checkbox"
                          />
                          <span :class="$style.checkboxLabel"
                            >Leadership Skills</span
                          >
                        </label>

                        <label :class="$style.checkboxOption">
                          <input
                            v-model="formData.stageFocusArea"
                            type="checkbox"
                            value="communication"
                            :class="$style.checkbox"
                          />
                          <span :class="$style.checkboxLabel"
                            >Communication Skills</span
                          >
                        </label>

                        <label :class="$style.checkboxOption">
                          <input
                            v-model="formData.stageFocusArea"
                            type="checkbox"
                            value="initial-screening"
                            :class="$style.checkbox"
                          />
                          <span :class="$style.checkboxLabel"
                            >Initial Screening</span
                          >
                        </label>

                        <label :class="$style.checkboxOption">
                          <input
                            v-model="formData.stageFocusArea"
                            type="checkbox"
                            value="executive"
                            :class="$style.checkbox"
                          />
                          <span :class="$style.checkboxLabel">Executive</span>
                        </label>

                        <label :class="$style.checkboxOption">
                          <input
                            v-model="formData.stageFocusArea"
                            type="checkbox"
                            value="other"
                            :class="$style.checkbox"
                          />
                          <span :class="$style.checkboxLabel">Other</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-if="showTechnicalInterviewBox"
                  :class="$style.conditionalBox"
                >
                  <div :class="$style.conditionalBoxContent">
                    <h4 :class="$style.conditionalBoxTitle">
                      Decide the technical interview structure
                    </h4>
                    <p :class="$style.conditionalBoxText">
                      Define the structure of this interview to ensure a
                      consistent and effective evaluation. We’ll help you choose
                      the most effective format based on your role. Add the
                      interview architect you select in the goal of this stage.
                    </p>
                    <a
                      href="https://getyogen.com/resources/technical-interview-methods"
                      target="_blank"
                      rel="noopener noreferrer"
                      :class="$style.conditionalBoxLink"
                    >
                      Technical Interview Architect
                      <ExternalLinkIcon :class="$style.externalLinkIcon" />
                    </a>
                  </div>
                </div>

                <div
                  v-if="showExecutiveBox"
                  :class="$style.conditionalBox"
                >
                  <div :class="$style.conditionalBoxContent">
                    <h4 :class="$style.conditionalBoxTitle">
                      Decide the technical interview structure
                    </h4>
                    <p :class="$style.conditionalBoxText">
                      Define the structure of this interview to ensure a
                      consistent and effective evaluation. We'll help you choose
                      the most effective format based on your role.
                    </p>
                    <a
                      href="https://getyogen.com/resources/hiring-decision-models-comparison"
                      target="_blank"
                      rel="noopener noreferrer"
                      :class="$style.conditionalBoxLink"
                    >
                      Hiring Decision Matrix
                      <ExternalLinkIcon :class="$style.externalLinkIcon" />
                    </a>
                  </div>
                </div>

                <div :class="$style.fieldGroup">
                  <label :class="$style.label">
                    <span :class="$style.questionNumber">3.</span>
                    What is the goal for this interview stage?
                  </label>
                  <textarea
                    v-model="formData.goal"
                    :class="$style.textarea"
                    placeholder="...."
                    rows="2"
                  ></textarea>
                </div>

                <div :class="$style.fieldGroup">
                  <label :class="$style.label">
                    <span :class="$style.questionNumber">4.</span>
                    What type of interview stage is this?
                    <span :class="$style.required">*</span>
                  </label>
                  <div :class="$style.radioGroup">
                    <label
                      :class="[
                        $style.radioOption,
                        formData.type === 'phone' && $style.selected,
                      ]"
                    >
                      <input
                        v-model="formData.type"
                        type="radio"
                        value="phone"
                        :class="$style.radioInput"
                      />
                      <span :class="$style.radioCircle"></span>
                      <span :class="$style.radioLabel">Phone call</span>
                    </label>
                    <label
                      :class="[
                        $style.radioOption,
                        formData.type === 'video' && $style.selected,
                      ]"
                    >
                      <input
                        v-model="formData.type"
                        type="radio"
                        value="video"
                        :class="$style.radioInput"
                      />
                      <span :class="$style.radioCircle"></span>
                      <span :class="$style.radioLabel">Video interview</span>
                    </label>
                    <label
                      :class="[
                        $style.radioOption,
                        formData.type === 'onsite' && $style.selected,
                      ]"
                    >
                      <input
                        v-model="formData.type"
                        type="radio"
                        value="onsite"
                        :class="$style.radioInput"
                      />
                      <span :class="$style.radioCircle"></span>
                      <span :class="$style.radioLabel">On-site interview</span>
                    </label>
                  </div>
                </div>

                <div :class="$style.fieldGroup">
                  <label :class="$style.label">
                    <span :class="$style.questionNumber">5.</span>
                    What is the duration of this interview (in minutes)?
                    <span :class="$style.required">*</span>
                  </label>
                  <input
                    v-model.number="formData.duration"
                    type="number"
                    :class="$style.input"
                    placeholder="45"
                    min="1"
                  />
                </div>

                <div :class="$style.fieldGroup">
                  <label :class="$style.checkboxLabel">
                    <input
                      v-model="formData.useAiTools"
                      type="checkbox"
                      :class="$style.checkbox"
                    />
                    <span :class="$style.checkboxText"
                      >Use AI tools to evaluate candidates in this
                      interview.</span
                    >
                    <InfoIcon :class="$style.infoIcon" />
                  </label>
                </div>

                <h3 :class="$style.sectionTitle">Required Interviewers</h3>

                <!-- Required Interviewers -->
                <div
                  v-for="(interviewer, index) in formData.interviewers.filter(
                    (i) => !i.isBackup,
                  )"
                  :key="index"
                  :class="$style.interviewerSection"
                >
                  <div :class="$style.fieldGroup">
                    <div :class="$style.labelRow">
                      <label :class="$style.label">
                        <span :class="$style.questionNumber"
                          >{{ 6 + index * 3 }}.</span
                        >
                        Interviewer Name <span :class="$style.required">*</span>
                      </label>
                      <button
                        v-if="formData.interviewers.length > 1"
                        type="button"
                        :class="$style.removeLink"
                        @click="removeInterviewerByIndex(index, false)"
                      >
                        Remove
                      </button>
                    </div>
                    <input
                      v-model="interviewer.name"
                      type="text"
                      :class="[
                        $style.input,
                        !interviewer.name &&
                          showValidation &&
                          $style.inputError,
                      ]"
                      placeholder="Role and/or Name of Interviewer"
                    />
                    <span
                      v-if="!interviewer.name && showValidation"
                      :class="$style.errorMessage"
                    >
                      <ErrorIcon :class="$style.errorIcon" />
                      Enter interviewer name to continue
                    </span>
                  </div>

                  <div :class="$style.fieldGroup">
                    <label :class="$style.label">
                      <span :class="$style.questionNumber"
                        >{{ 7 + index * 3 }}.</span
                      >
                      Interviewer's LinkedIn profile URL (optional — may be
                      shared in the Candidate Packet)
                    </label>
                    <input
                      v-model="interviewer.linkedIn"
                      type="url"
                      :class="$style.input"
                      placeholder="https://www.linkedin.com/in/name"
                    />
                  </div>

                  <div :class="$style.fieldGroup">
                    <label :class="$style.label">
                      <span :class="$style.questionNumber"
                        >{{ 8 + index * 3 }}.</span
                      >
                      What is the interviewer's primary focus area/s?
                    </label>
                    <select
                      v-model="interviewer.focusArea"
                      :class="$style.select"
                    >
                      <option value="">Select a focus area</option>
                      <option
                        v-for="area in formData.stageFocusArea"
                        :key="area"
                        :value="area"
                      >
                        {{
                          focusAreaOptions.find((opt) => opt.value === area)
                            ?.label || area
                        }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Backup Interviewers Section -->
                <h3
                  v-if="formData.interviewers.some((i) => i.isBackup)"
                  :class="$style.sectionTitle"
                >
                  Backup Interviewers
                </h3>

                <!-- Backup Interviewers -->
                <div
                  v-for="(interviewer, index) in formData.interviewers.filter(
                    (i) => i.isBackup,
                  )"
                  :key="'backup-' + index"
                  :class="$style.interviewerSection"
                >
                  <div :class="$style.fieldGroup">
                    <div :class="$style.labelRow">
                      <label :class="$style.label">
                        <span :class="$style.questionNumber"
                          >{{ 6 + index * 3 }}.</span
                        >
                        Interviewer Name <span :class="$style.required">*</span>
                      </label>
                      <button
                        type="button"
                        :class="$style.removeLink"
                        @click="removeInterviewerByIndex(index, true)"
                      >
                        Remove
                      </button>
                    </div>
                    <input
                      v-model="interviewer.name"
                      type="text"
                      :class="[
                        $style.input,
                        !interviewer.name &&
                          showValidation &&
                          $style.inputError,
                      ]"
                      placeholder="Role and/or Name of Interviewer"
                    />
                    <span
                      v-if="!interviewer.name && showValidation"
                      :class="$style.errorMessage"
                    >
                      <ErrorIcon :class="$style.errorIcon" />
                      Enter interviewer name to continue
                    </span>
                  </div>

                  <div :class="$style.fieldGroup">
                    <label :class="$style.label">
                      <span :class="$style.questionNumber"
                        >{{ 7 + index * 3 }}.</span
                      >
                      Interviewer's LinkedIn profile URL (optional — may be
                      shared in the Candidate Packet)
                    </label>
                    <input
                      v-model="interviewer.linkedIn"
                      type="url"
                      :class="$style.input"
                      placeholder="https://www.linkedin.com/in/name"
                    />
                  </div>

                  <div :class="$style.fieldGroup">
                    <label :class="$style.label">
                      <span :class="$style.questionNumber"
                        >{{ 8 + index * 3 }}.</span
                      >
                      What is the interviewer's primary focus area/s?
                    </label>
                    <select
                      v-model="interviewer.focusArea"
                      :class="$style.select"
                    >
                      <option value="">Select a focus area</option>
                      <option
                        v-for="area in formData.stageFocusArea"
                        :key="area"
                        :value="area"
                      >
                        {{
                          focusAreaOptions.find((opt) => opt.value === area)
                            ?.label || area
                        }}
                      </option>
                    </select>
                  </div>
                </div>

                <div :class="$style.addInterviewerLinks">
                  <button
                    type="button"
                    :class="$style.addLink"
                    @click="addInterviewer(false)"
                  >
                    + Add another interviewer
                  </button>
                  <button
                    type="button"
                    :class="$style.addLink"
                    @click="addInterviewer(true)"
                  >
                    + Add backup interviewer
                  </button>
                </div>

                <div :class="$style.fieldGroup">
                  <label :class="$style.label">
                    <span :class="$style.questionNumber">9.</span>
                    Estimated Time to Next Stage (in days)
                  </label>
                  <p :class="$style.fieldDescription">
                    (Estimated time for a candidate to move to the next stage.
                    Shorter gaps indicate healthier, faster pipelines)
                  </p>
                  <input
                    v-model.number="formData.daysToNext"
                    type="number"
                    :class="$style.input"
                    min="0"
                    placeholder="7"
                  />
                  <p :class="$style.helperText">
                    Typing in 0 or leaving the section blank will result in this
                    and the next stage being on the same day, represented in the
                    overview as being stacked on top of each other.
                  </p>
                </div>

                <button
                  type="button"
                  :class="$style.addStageLink"
                  @click="$emit('addStage')"
                >
                  + Add Stage
                </button>

                <div :class="$style.formActions">
                  <button
                    v-if="isEditing"
                    type="button"
                    :class="$style.deleteButton"
                    @click="$emit('delete')"
                  >
                    Delete Stage
                  </button>
                  <button
                    type="submit"
                    :class="$style.saveButton"
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
            <div :class="$style.sidebar">
              <div :class="$style.sidebarHeader">
                <span :class="$style.sidebarBadge">Best Practices</span>
              </div>

              <p :class="$style.sidebarDescription">
                Fill out the fields to get instant feedback from the Unicorn
                Checker.
              </p>

              <div
                v-if="checkResults.warnings.length > 0"
                :class="$style.warningsSection"
              >
                <h4 :class="$style.resultsTitle">
                  <WarningIcon :class="$style.warningTitleIcon" />
                  Warnings
                </h4>
                <div :class="$style.warningCard">
                  <p
                    v-for="(warning, idx) in checkResults.warnings"
                    :key="idx"
                    :class="$style.warningText"
                  >
                    {{ warning }}
                  </p>
                </div>
              </div>

              <div
                v-if="checkResults.strengths.length > 0"
                :class="$style.strengthsSection"
              >
                <h4 :class="$style.resultsTitle">
                  <CheckIcon :class="$style.strengthTitleIcon" />
                  Strengths
                </h4>
                <div :class="$style.strengthCards">
                  <div
                    v-for="(strength, idx) in checkResults.strengths"
                    :key="idx"
                    :class="$style.strengthCard"
                  >
                    <p :class="$style.strengthText">{{ strength }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
  import { ref, reactive, onMounted, computed, watch, onUnmounted } from 'vue'
  import ImpactModelSummary from './ImpactModelSummary.vue'
  import {
    calculateImpactModelV1,
    validateImpactModelInputsV1,
    type ImpactModelInputsV1,
  } from '~/utils/impactModelV1'
  import CloseIcon from '~/assets/svg/close.svg'
  import WarningIcon from '~/assets/svg/warning.svg'
  import { analyzeInterviewPlanWarnings } from '~/utils/interviewPlanWarnings'
  import CheckIcon from '~/assets/svg/checkmarkCircle.svg'
  import InfoIcon from '~/assets/svg/info.svg'
  import ExternalLinkIcon from '~/assets/svg/externalLink.svg'
  import ErrorIcon from '~/assets/svg/error.svg'

  interface Interviewer {
    name: string
    linkedIn?: string
    focusArea: string
    customFocusArea?: string
    trainedByYogen: boolean
    isBackup?: boolean
  }

  interface Stage {
    id: string
    name: string
    goal: string
    type: 'phone' | 'video' | 'onsite'
    duration: number
    useAiTools: boolean
    stageFocusArea?: string | string[]
    interviewers: Interviewer[]
    daysToNext: number
    cost: number
    warnings: string[]
  }

  interface CheckResults {
    warnings: string[]
    strengths: string[]
  }

  const props = defineProps<{
    stage?: Stage
    stageNumber?: number
    planStages?: Stage[]
    impactInputs?: Partial<ImpactModelInputsV1>
  }>()

  const emit = defineEmits<{
    close: []
    save: [stage: Omit<Stage, 'id' | 'cost' | 'warnings'>]
    delete: []
    addStage: []
  }>()

  function handleClose() {
    emit('close')
  }

  const isEditing = computed(() => !!props.stage)
  const showValidation = ref(false)
  const isFocusAreaOpen = ref(false)

  const formData = reactive({
    name: '',
    goal: '',
    type: 'video' as 'phone' | 'video' | 'onsite',
    duration: 45,
    useAiTools: false,
    stageFocusArea: [] as string[],
    interviewers: [
      {
        name: '',
        linkedIn: '',
        focusArea: '',
        customFocusArea: '',
        trainedByYogen: true,
        isBackup: false,
      },
    ] as Interviewer[],
    daysToNext: 7,
  })

  const checkResults = reactive<CheckResults>({
    warnings: [],
    strengths: [],
  })

  const focusAreaOptions = [
    { value: 'technical-interview', label: 'Technical Skills' },
    { value: 'cultural-fit', label: 'Cultural Fit' },
    { value: 'behavioral', label: 'Behavioral Skills' },
    { value: 'leadership', label: 'Leadership Skills' },
    { value: 'communication', label: 'Communication Skills' },
    { value: 'initial-screening', label: 'Initial Screening' },
    { value: 'executive', label: 'Executive' },
    { value: 'other', label: 'Other' },
  ]

  const selectedFocusAreasText = computed(() => {
    if (formData.stageFocusArea.length === 0) return ''

    // Map selected values to their labels
    const selectedLabels = formData.stageFocusArea.map((value) => {
      const option = focusAreaOptions.find((opt) => opt.value === value)
      return option ? option.label : value
    })

    if (formData.stageFocusArea.length === 1) {
      return selectedLabels[0]
    }

    // Join with commas for multiple selections
    return selectedLabels.join(', ')
  })

  const showTechnicalInterviewBox = computed(() => {
    return (
      formData.stageFocusArea.includes('technical-interview') ||
      formData.stageFocusArea.includes('technical-coding') ||
      formData.stageFocusArea.includes('system-design')
    )
  })

  const showExecutiveBox = computed(() => {
    return formData.stageFocusArea.includes('executive')
  })

  function toggleFocusAreaDropdown() {
    isFocusAreaOpen.value = !isFocusAreaOpen.value
  }

  function closeFocusAreaDropdown() {
    isFocusAreaOpen.value = false
  }

  function clearAllFocusAreas() {
    formData.stageFocusArea = []
  }

  function handleClickOutside(event: MouseEvent) {
    const dropdownRef = document.querySelector('[data-custom-dropdown]') // Add data attribute
    if (dropdownRef && !dropdownRef.contains(event.target as Node)) {
      closeFocusAreaDropdown()
    }
  }

  onMounted(() => {
    if (props.stage) {
      formData.name = props.stage.name
      formData.goal = props.stage.goal
      formData.type = props.stage.type
      formData.duration = props.stage.duration
      formData.useAiTools = props.stage.useAiTools
      // Handle both string and array formats
      if (props.stage.stageFocusArea) {
        if (Array.isArray(props.stage.stageFocusArea)) {
          formData.stageFocusArea = props.stage.stageFocusArea
        } else {
          formData.stageFocusArea = [props.stage.stageFocusArea]
        }
      } else {
        formData.stageFocusArea = []
      }
      formData.interviewers =
        props.stage.interviewers.length > 0
          ? props.stage.interviewers.map((i) => ({
              ...i,
              customFocusArea: i.customFocusArea || '',
              trainedByYogen:
                i.trainedByYogen !== undefined ? i.trainedByYogen : true,
            }))
          : [
              {
                name: '',
                linkedIn: '',
                focusArea: '',
                customFocusArea: '',
                trainedByYogen: true,
                isBackup: false,
              },
            ]
      formData.daysToNext = props.stage.daysToNext
    }
    updateCheckResults()

    // Add click outside listener
    document.addEventListener('click', handleClickOutside)
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
  })

  watch(
    formData,
    () => {
      updateCheckResults()
    },
    { deep: true },
  )

  const previewStages = computed(() => {
    const stageId = props.stage?.id ?? '__draft-stage__'
    const draftStage: Stage = {
      id: stageId,
      ...formData,
      cost: props.stage?.cost ?? 0,
      warnings: [],
    }
    const planStages = [...(props.planStages ?? [])]
    const existingIndex = planStages.findIndex((stage) => stage.id === stageId)
    if (existingIndex >= 0) {
      planStages[existingIndex] = draftStage
    } else {
      const insertionIndex = Math.max(
        0,
        Math.min(
          (props.stageNumber ?? planStages.length + 1) - 1,
          planStages.length,
        ),
      )
      planStages.splice(insertionIndex, 0, draftStage)
    }

    return planStages
  })

  const previewImpact = computed(() => {
    if (validateImpactModelInputsV1(props.impactInputs).length > 0) return null
    return calculateImpactModelV1(
      props.impactInputs as ImpactModelInputsV1,
      previewStages.value,
    )
  })

  function updateCheckResults() {
    const strengths: string[] = []
    const stageId = props.stage?.id ?? '__draft-stage__'
    const warnings =
      analyzeInterviewPlanWarnings(previewStages.value)[stageId] ?? []

    if (
      formData.interviewers.filter((i) => !i.isBackup && i.name).length === 1
    ) {
      strengths.push("One 'Required' interviewer: Efficient and focused.")
    }

    if (formData.daysToNext === 7) {
      strengths.push(
        'Estimated Days to Next Stage (7 days): Optimal. Keeps candidate warm and process moving efficiently.',
      )
    }

    checkResults.warnings = warnings
    checkResults.strengths = strengths
  }

  function addInterviewer(isBackup: boolean) {
    formData.interviewers.push({
      name: '',
      linkedIn: '',
      focusArea: '',
      customFocusArea: '',
      trainedByYogen: true,
      isBackup,
    })
  }

  function removeInterviewer(index: number) {
    formData.interviewers.splice(index, 1)
  }

  function removeInterviewerByIndex(index: number, isBackup: boolean) {
    // Find the actual index in the formData.interviewers array
    const interviewerList = isBackup
      ? formData.interviewers.filter((i) => i.isBackup)
      : formData.interviewers.filter((i) => !i.isBackup)

    const actualIndex = formData.interviewers.indexOf(interviewerList[index])
    if (actualIndex !== -1) {
      removeInterviewer(actualIndex)
    }
  }

  function handleSave() {
    showValidation.value = true

    const hasEmptyInterviewer = formData.interviewers.some(
      (i) => !i.name.trim(),
    )
    if (hasEmptyInterviewer || !formData.name.trim()) {
      return
    }

    emit('save', {
      name: formData.name,
      goal: formData.goal,
      type: formData.type,
      duration: formData.duration,
      useAiTools: formData.useAiTools,
      stageFocusArea: formData.stageFocusArea,
      interviewers: formData.interviewers.filter((i) => i.name.trim() !== ''),
      daysToNext: formData.daysToNext,
    })
  }
</script>

<style module>
  .overlay {
    position: fixed;
    inset: 0;
    background: color-mix(in srgb, var(--color-text-primary) 50%, transparent);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    z-index: 1000;
    padding: var(--space-24);
    overflow-y: auto;
  }

  .modalContainer {
    display: flex;
    gap: var(--space-24);
    max-width: 900px;
    width: 100%;
    margin: auto;
  }

  .modal {
    flex: 1;
    background: var(--color-surface-default);
    border-radius: var(--radius-md);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    max-width: 900px;
    max-height: calc(100dvh - 48px);
    position: relative;
  }

  .previewHint {
    padding: var(--space-12);
    margin: 0;
  }

  .header {
    flex-shrink: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--space-16) var(--space-24);
    background: var(--color-interactive-primary);
  }

  .headerTitle {
    font: var(--type-body-large-emphasized);
    letter-spacing: var(--type-body-large-emphasized-tracking);
    color: var(--color-text-on-brand);
    margin: 0;
  }

  .closeButton {
    background: transparent;
    border: none;
    padding: var(--space-4);
    cursor: pointer;
    border-radius: var(--radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .closeIcon {
    width: 20px;
    height: 20px;
    fill: var(--color-icon-on-brand);
  }

  .container {
    display: flex;
    flex-direction: row;
    flex: 1;
    overflow-y: auto;
    min-height: 0;
    padding: var(--space-24);
    gap: var(--space-8);
  }

  .form {
    display: flex;
    flex-direction: column;
    gap: var(--space-24);
    border: none;
    padding: 0;
    padding-bottom: calc(var(--space-48) + var(--space-48) + var(--space-24));
  }

  .fieldGroup {
    display: flex;
    flex-direction: column;
    gap: var(--space-8);
  }

  .labelRow {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .label {
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
    color: var(--color-text-primary);
    display: flex;
    align-items: center;
    gap: var(--space-4);
    flex-wrap: wrap;
  }

  .questionNumber {
    font: var(--type-body-medium-emphasized);
    letter-spacing: var(--type-body-medium-emphasized-tracking);
  }

  .required {
    color: var(--color-status-error);
  }

  .infoIcon {
    width: 16px;
    height: 16px;
    fill: var(--color-text-secondary);
    cursor: help;
  }

  .input {
    padding: var(--space-16);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-sm);
    font: var(--type-body-large);
    letter-spacing: var(--type-body-large-tracking);
    color: var(--color-text-primary);
    transition: border-color 0.2s ease;
    max-width: 540px;
  }

  .input:focus {
    outline: none;
    border-color: var(--color-text-primary);
  }

  .input::placeholder {
    color: var(--color-text-secondary);
  }

  .inputError {
    border-color: var(--color-status-error);
    background: var(--color-status-error-subtle);
  }

  .errorMessage {
    display: flex;
    align-items: center;
    gap: var(--space-8);
    font: var(--type-body-small);
    letter-spacing: var(--type-body-small-tracking);
    color: var(--color-status-error);
  }

  .errorIcon {
    width: 16px;
    height: 16px;
  }

  .textarea {
    padding: var(--space-16);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-sm);
    font: var(--type-body-large);
    letter-spacing: var(--type-body-large-tracking);
    color: var(--color-text-primary);
    resize: vertical;
    font-family: inherit;
    max-width: 540px;
    width: 100%;
  }

  .textarea:focus {
    outline: none;
    border-color: var(--color-text-primary);
  }

  .textarea::placeholder {
    color: var(--color-text-secondary);
  }

  .select {
    padding: var(--space-16);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-sm);
    font: var(--type-body-large);
    letter-spacing: var(--type-body-large-tracking);
    color: var(--color-text-primary);
    background: var(--color-surface-default);
    cursor: pointer;
    max-width: 540px;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23707070' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 20px;
    padding-right: var(--space-48);
  }

  .select:focus {
    outline: none;
    border-color: var(--color-text-primary);
  }

  /* Custom Dropdown Styles */
  .customDropdown {
    position: relative;
    max-width: 540px;
    z-index: 1001;
  }

  .dropdownTrigger {
    padding: var(--space-16);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-sm);
    font: var(--type-body-large);
    letter-spacing: var(--type-body-large-tracking);
    color: var(--color-text-primary);
    background: var(--color-surface-default);
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: border-color 0.2s ease;
  }

  .dropdownTrigger:hover {
    border-color: var(--color-border-default);
  }

  .dropdownOpen {
    border-color: var(--color-text-primary);
  }

  .dropdownPlaceholder {
    color: var(--color-text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
  }

  .dropdownArrow {
    width: 20px;
    height: 20px;
    color: var(--color-text-secondary);
    transition: transform 0.2s ease;
    flex-shrink: 0;
    margin-left: var(--space-8);
  }

  .arrowRotated {
    transform: rotate(180deg);
  }

  .dropdownContent {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: var(--color-surface-default);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-sm);
    box-shadow: var(--elevation-md);
    z-index: 1002;
    max-height: 400px;
    overflow-y: auto;
  }

  .dropdownHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--space-16);
    border-bottom: 1px solid var(--color-border-default);
    position: sticky;
    top: 0;
    background: var(--color-surface-default);
  }

  .dropdownTitle {
    font: var(--type-label-large);
    letter-spacing: var(--type-label-large-tracking);
    color: var(--color-text-primary);
  }

  .clearAllButton {
    composes: text-link from global;
    background: transparent;
    border: none;
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
    color: var(--color-interactive-link);
    cursor: pointer;
    padding: var(--space-4) var(--space-8);
  }

  .clearAllButton:hover {
    text-decoration: underline;
  }

  .checkboxOptionsContainer {
    padding: var(--space-16);
    display: flex;
    flex-direction: column;
    gap: var(--space-12);
  }

  .checkboxOption {
    display: flex;
    align-items: flex-start;
    gap: var(--space-12);
    cursor: pointer;
    padding: var(--space-4) 0;
  }

  .checkbox {
    width: 20px;
    height: 20px;
    accent-color: var(--color-interactive-primary);
    flex-shrink: 0;
    margin-top: var(--space-2);
  }

  .checkboxLabel {
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
    color: var(--color-text-primary);
    flex: 1;
  }

  .conditionalBox {
    background: linear-gradient(
      135deg,
      var(--color-surface-accent) 0%,
      var(--color-surface-accent) 100%
    );
    border-radius: var(--radius-md);
    padding: var(--space-24);
    max-width: 540px;
  }

  .conditionalBoxContent {
    display: flex;
    flex-direction: column;
    gap: var(--space-8);
  }

  .conditionalBoxTitle {
    font: var(--type-title-small);
    letter-spacing: var(--type-title-small-tracking);
    color: var(--color-text-primary);
    margin: 0;
  }

  .conditionalBoxText {
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
    color: var(--color-text-primary);
    margin: 0;
  }

  .conditionalBoxLink {
    composes: text-link from global;
    display: inline-flex;
    align-items: center;
    gap: var(--space-8);
    font: var(--type-label-large);
    letter-spacing: var(--type-label-large-tracking);
    color: var(--color-interactive-link);
    text-decoration: none;
  }

  .conditionalBoxLink:hover {
    text-decoration: underline;
  }

  .externalLinkIcon {
    width: 16px;
    height: 16px;
    fill: var(--color-interactive-link);
  }

  .radioGroup {
    display: flex;
    flex-direction: column;
    gap: var(--space-8);
    padding-left: var(--space-24);
  }

  .radioOption {
    display: flex;
    align-items: center;
    gap: var(--space-12);
    cursor: pointer;
  }

  .radioInput {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  .radioCircle {
    width: 20px;
    height: 20px;
    border: 2px solid var(--color-border-default);
    border-radius: var(--radius-full);
    position: relative;
    flex-shrink: 0;
    transition: border-color 0.2s ease;
  }

  .selected .radioCircle {
    border-color: var(--color-interactive-primary);
  }

  .selected .radioCircle::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 10px;
    height: 10px;
    background: var(--color-interactive-primary);
    border-radius: var(--radius-full);
  }

  .radioLabel {
    font: var(--type-body-large);
    letter-spacing: var(--type-body-large-tracking);
    color: var(--color-text-primary);
  }

  .checkboxText {
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
    color: var(--color-text-primary);
  }

  .sectionTitle {
    font: var(--type-title-small);
    letter-spacing: var(--type-title-small-tracking);
    color: var(--color-text-primary);
    margin: var(--space-8) 0 0 0;
  }

  .interviewerSection {
    display: flex;
    flex-direction: column;
    gap: var(--space-16);
  }

  .removeLink {
    background: transparent;
    border: none;
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
    color: var(--color-text-secondary);
    cursor: pointer;
    padding: 0;
  }

  .removeLink:hover {
    color: var(--color-status-error);
    text-decoration: underline;
  }

  .addInterviewerLinks {
    display: flex;
    gap: var(--space-20);
  }

  .addLink {
    composes: text-link from global;
    background: transparent;
    border: none;
    font: var(--type-label-large);
    letter-spacing: var(--type-label-large-tracking);
    color: var(--color-interactive-link);
    cursor: pointer;
    padding: 0;
  }

  .addLink:hover {
    text-decoration: underline;
  }

  .fieldDescription {
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
    color: var(--color-text-secondary);
    margin: 0;
  }

  .helperText {
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
    color: var(--color-text-secondary);
    margin: 0;
    max-width: 540px;
  }

  .addStageLink {
    composes: text-link from global;
    background: transparent;
    border: none;
    font: var(--type-label-large);
    letter-spacing: var(--type-label-large-tracking);
    color: var(--color-interactive-link);
    cursor: pointer;
    padding: 0;
    text-align: left;
  }

  .addStageLink:hover {
    text-decoration: underline;
  }

  .formActions {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: var(--space-16);
    position: absolute;
    bottom: 25px;
    left: 50%;
    transform: translateX(-50%);
    width: auto;
  }

  .deleteButton {
    background: var(--color-surface-default);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-sm);
    padding: var(--space-12) var(--space-32);
    font: var(--type-body-large-emphasized);
    letter-spacing: var(--type-body-large-emphasized-tracking);
    color: var(--color-text-primary);
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 160px;
  }

  .deleteButton:hover {
    background: var(--color-surface-muted);
  }

  .saveButton {
    background: var(--color-interactive-primary);
    border: none;
    border-radius: var(--radius-sm);
    padding: var(--space-12) var(--space-32);
    font: var(--type-title-medium-emphasized);
    letter-spacing: var(--type-title-medium-emphasized-tracking);
    color: var(--color-text-on-brand);
    cursor: pointer;
    transition: background 0.2s ease;
    min-width: 160px;
  }

  .saveButton:hover {
    background: var(--color-interactive-primary);
  }

  .sidebar {
    width: 300px;
    background: var(--color-surface-default);
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border-default);
    padding: 0;
    display: flex;
    flex-direction: column;
    height: fit-content;
    flex-shrink: 0;
  }

  .sidebarHeader {
    padding: var(--space-12) var(--space-16);
    background-color: var(--color-interactive-primary);
    border-radius: var(--radius-md) var(--radius-md) 0 0;
  }

  .sidebarBadge {
    color: var(--color-text-on-brand);
  }

  .sidebarDescription {
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
    color: var(--color-text-primary);
    margin: 0;
    padding: var(--space-16);
  }

  .warningsSection {
    padding: 0 var(--space-16) var(--space-16);
  }

  .strengthsSection {
    padding: 0 var(--space-16) var(--space-16);
  }

  .resultsTitle {
    display: flex;
    align-items: center;
    gap: var(--space-8);
    font: var(--type-body-large-emphasized);
    letter-spacing: var(--type-body-large-emphasized-tracking);
    margin: 0 0 var(--space-8) 0;
    color: var(--color-text-primary);
  }

  .warningTitleIcon {
    width: 20px;
    height: 20px;
  }

  .strengthTitleIcon {
    width: 20px;
    height: 20px;
  }

  .warningCard {
    background: var(--color-status-warning-subtle);
    border-radius: var(--radius-sm);
    padding: var(--space-12) var(--space-16);
    border-left: 2px var(--color-status-warning) solid;
  }

  .warningText {
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
    color: var(--color-text-primary);
    margin: 0;
  }

  .strengthCards {
    display: flex;
    flex-direction: column;
    gap: var(--space-8);
  }

  .strengthCard {
    background: var(--color-status-success-subtle);
    border-radius: var(--radius-sm);
    padding: var(--space-12) var(--space-16);
    border-left: 2px var(--color-status-success) solid;
  }

  .strengthText {
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
    color: var(--color-text-primary);
    margin: 0;
  }

  @media (max-width: 900px) {
    .modalContainer {
      flex-direction: column;
    }

    .modal {
      max-width: 100%;
    }

    .sidebar {
      width: 100%;
    }

    .dropdownContent {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 90%;
      max-width: 500px;
      max-height: 70vh;
    }
  }
</style>
